import { z } from 'zod';
import { ColorSchema } from './color.js';
import { EventSetSchema } from './events.js';
import { MAX_YEAR, MIN_YEAR, isValidDateKey } from './dates.js';

export const SCHEMA_VERSION = 6;

export const RectSchema = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
export type Rect = z.infer<typeof RectSchema>;

export const StrokeSchema = z.object({ widthPt: z.number().min(0).max(50), color: ColorSchema });
export type Stroke = z.infer<typeof StrokeSchema>;

export const TextStyleSchema = z.object({
  /** Exact font asset id, e.g. "inter-700" (curated) or an uploaded font id. */
  fontId: z.string().min(1),
  sizePt: z.number().min(2).max(400),
  color: ColorSchema,
  align: z.enum(['left', 'center', 'right']),
  letterSpacingPt: z.number().min(-5).max(50),
  lineHeight: z.number().min(0.6).max(4),
  uppercase: z.boolean(),
});
export type TextStyle = z.infer<typeof TextStyleSchema>;

export const PanelSchema = z.object({
  fill: ColorSchema,
  opacity: z.number().min(0).max(1),
  radiusMm: z.number().min(0).max(100),
  paddingMm: z.number().min(0).max(100),
});
export type Panel = z.infer<typeof PanelSchema>;

const base = {
  id: z.string().min(1),
  name: z.string(),
  rect: RectSchema,
  hidden: z.boolean().optional(),
};

export const TextElSchema = z.object({
  ...base,
  type: z.literal('text'),
  role: z.enum(['heading', 'subheading', 'caption', 'footer', 'contact', 'custom']),
  /** Tokens: {MONTH} {MONTH_SHORT} {YEAR} {CAPTION} {CONTACT} {CUSTOMER} */
  text: z.string().max(2000),
  style: TextStyleSchema,
  valign: z.enum(['top', 'middle', 'bottom']),
  panel: PanelSchema.nullable(),
});
export const ImageElSchema = z.object({
  ...base,
  type: z.literal('image'),
  /** Photo slot index on the page (collages have several). */
  slot: z.number().int().min(0).max(20),
  radiusMm: z.number().min(0).max(200),
  border: StrokeSchema.nullable(),
  required: z.boolean(),
  /** Same photograph on every page (e.g. an estate agent's portrait); stored under photos.shared. */
  shared: z.boolean().optional(),
});
export const ShapeElSchema = z.object({
  ...base,
  type: z.literal('shape'),
  fill: ColorSchema.nullable(),
  opacity: z.number().min(0).max(1),
  radiusMm: z.number().min(0).max(200),
  stroke: StrokeSchema.nullable(),
});
export const LogoElSchema = z.object({ ...base, type: z.literal('logo'), align: z.enum(['left', 'center', 'right']) });

export const DayStyleSchema = z.object({ fill: ColorSchema.nullable(), textColor: ColorSchema.nullable() });
export const GridStyleSchema = z.object({
  outerBorder: StrokeSchema.nullable(),
  innerLines: StrokeSchema.nullable(),
  cellFill: ColorSchema.nullable(),
  cellPaddingMm: z.number().min(0).max(20),
  datePosition: z.enum(['top-left', 'top-center', 'top-right', 'center', 'bottom-right']),
  header: z.object({
    heightMm: z.number().min(0).max(100),
    fill: ColorSchema.nullable(),
    text: TextStyleSchema,
    labels: z.enum(['short', 'narrow', 'full']),
  }),
  dateText: TextStyleSchema,
  adjacentText: TextStyleSchema,
  eventText: TextStyleSchema,
  saturday: DayStyleSchema,
  sunday: DayStyleSchema,
  weekNumbers: z.object({ text: TextStyleSchema, widthMm: z.number().min(3).max(40) }),
});
export type GridStyle = z.infer<typeof GridStyleSchema>;
export const GridElSchema = z.object({ ...base, type: z.literal('grid'), style: GridStyleSchema });
export const MiniElSchema = z.object({
  ...base,
  type: z.literal('mini'),
  which: z.enum(['prev', 'next']),
  title: TextStyleSchema,
  text: TextStyleSchema,
});
export const LegendElSchema = z.object({ ...base, type: z.literal('legend'), style: TextStyleSchema });

/** Year-at-a-glance: all 12 months of the year as mini calendars. */
export const YearStyleSchema = z.object({
  title: TextStyleSchema,
  titleFill: ColorSchema.nullable(),
  titleRadiusMm: z.number().min(0).max(50),
  titleHeightMm: z.number().min(0).max(200),
  weekday: TextStyleSchema,
  weekdayLabels: z.enum(['narrow', 'short']),
  date: TextStyleSchema,
  saturdayColor: ColorSchema.nullable(),
  sundayColor: ColorSchema.nullable(),
  weekendFill: ColorSchema.nullable(),
  shadeRadiusMm: z.number().min(0).max(20),
});
export type YearStyle = z.infer<typeof YearStyleSchema>;
export const YearGridElSchema = z.object({
  ...base,
  type: z.literal('yeargrid'),
  columns: z.number().int().min(1).max(12),
  gapXmm: z.number().min(0).max(200),
  gapYmm: z.number().min(0).max(200),
  style: YearStyleSchema,
});
/** Colour key for shaded event sets ("■ Public holidays  ■ School holidays"). */
export const KeyElSchema = z.object({ ...base, type: z.literal('key'), style: TextStyleSchema, align: z.enum(['left', 'center', 'right']) });

/**
 * List of public holidays, e.g. "01/01 - New Year's Day" one per line (column)
 * or "01/01 - New Year's Day | 22/03 - Human Rights Day* | …" (row).
 * A Sunday holiday is listed on its observed Monday with a marker (*).
 */
export const HolidayListElSchema = z.object({
  ...base,
  type: z.literal('holidays'),
  /** which event-set kinds to list: public holidays, school holidays, birthdays (personal), business, other */
  kinds: z.array(z.enum(['public', 'school', 'personal', 'business', 'custom'])).min(1),
  style: TextStyleSchema,
  layout: z.enum(['column', 'row']),
  /** column layout: number of columns the list is split into */
  columns: z.number().int().min(1).max(6),
  dateFormat: z.enum(['numeric', 'short-month']),
  /** short: e.g. "Women's Day" instead of "National Women's Day" */
  names: z.enum(['short', 'full']),
  /** year: every holiday of the calendar year; page: only the months on this page (a month, a tent face) */
  range: z.enum(['year', 'page']),
  /** marker: a Sunday holiday is shown on its observed Monday with the marker; both: list both days */
  observed: z.enum(['marker', 'both']),
  marker: z.string().max(4),
  separator: z.string().max(10),
  /** line under the list when a marked holiday is shown ("" for none) */
  footnote: z.string().max(200),
});
export type HolidayListEl = z.infer<typeof HolidayListElSchema>;

export const ElementSchema = z.discriminatedUnion('type', [
  TextElSchema,
  ImageElSchema,
  ShapeElSchema,
  LogoElSchema,
  GridElSchema,
  MiniElSchema,
  LegendElSchema,
  YearGridElSchema,
  KeyElSchema,
  HolidayListElSchema,
]);
export type Element = z.infer<typeof ElementSchema>;
export type TextEl = z.infer<typeof TextElSchema>;
export type ImageEl = z.infer<typeof ImageElSchema>;
export type ShapeEl = z.infer<typeof ShapeElSchema>;
export type LogoEl = z.infer<typeof LogoElSchema>;
export type GridEl = z.infer<typeof GridElSchema>;
export type MiniEl = z.infer<typeof MiniElSchema>;
export type LegendEl = z.infer<typeof LegendElSchema>;
export type YearGridEl = z.infer<typeof YearGridElSchema>;
export type KeyEl = z.infer<typeof KeyElSchema>;
export type ElementType = Element['type'];

export const PageDesignSchema = z.object({ background: ColorSchema.nullable(), elements: z.array(ElementSchema) });
export type PageDesign = z.infer<typeof PageDesignSchema>;

/** Per-page patch over the global design: element id → partial element (deep-merged). */
export const PageOverrideSchema = z.object({
  background: ColorSchema.nullable().optional(),
  elements: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
});
export type PageOverride = z.infer<typeof PageOverrideSchema>;

export const ImagePlacementSchema = z.object({
  assetId: z.string().min(1),
  fit: z.enum(['fill', 'fit']),
  /** 1 = the base fill/fit size; >1 zooms in. */
  zoom: z.number().min(0.2).max(10),
  /** -1..1: -1 aligns the image's left/top edge to the frame, 1 its right/bottom edge. */
  panX: z.number().min(-1).max(1),
  panY: z.number().min(-1).max(1),
});
export type ImagePlacement = z.infer<typeof ImagePlacementSchema>;

export const ProductSchema = z.object({
  /**
   * monthly: 12 (or 13 with cover) month pages (wall, wiro desk, desk pad, tear-off leaves).
   * year: one year-at-a-glance page (optional reverse side).
   * tent: folded tent card — two display faces (Jan–Jun, Jul–Dec) composed into one flat sheet.
   */
  productType: z.enum(['monthly', 'year', 'tent']),
  /** Catalogue entry the project was created from (informational). */
  catalogId: z.string().max(60).optional(),
  sheetPreset: z.enum(['A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'DL', 'BC', 'D210', 'SQ150', 'custom']),
  /** Monthly: leaves printed both sides (e.g. 7-leaf wiro desk calendar: cover + 12 months + back cover). */
  duplex: z.boolean(),
  /** Tent card: flat sheet = face B (upside down) | face A | base | glue tab, top to bottom. */
  /** baseMm / tabMm: classic card; DL tents use 99 mm panels and a 15 mm tape strip (see tent.ts) */
  tent: z.object({ baseMm: z.number().min(0).max(300), tabMm: z.number().min(0).max(100), layout: z.enum(['classic', 'dl-landscape', 'dl-portrait-6', 'dl-portrait-4']) }),
  /** Tear-off pad: magnetic carrier card artwork with the area where the pad is glued. */
  carrier: z.object({
    enabled: z.boolean(),
    widthMm: z.number().min(30).max(1000),
    heightMm: z.number().min(30).max(1000),
    pad: z.object({ x: z.number(), y: z.number(), w: z.number().min(0), h: z.number().min(0) }),
  }),
  orientation: z.enum(['portrait', 'landscape']),
  widthMm: z.number().min(50).max(2000),
  heightMm: z.number().min(50).max(2000),
  includeCover: z.boolean(),
  backingBoard: z.object({ enabled: z.boolean(), printed: z.boolean(), note: z.string().max(500) }),
  bleedMm: z.number().min(0).max(20),
  safeMarginMm: z.number().min(0).max(50),
  /** top-bottom: clearance for a metal/tin rim crimped across top and bottom. */
  bindingEdge: z.enum(['top', 'left', 'top-bottom', 'none']),
  bindingClearanceMm: z.number().min(0).max(60),
  cropMarks: z.boolean(),
  finishing: z.string().max(500),
  calendarArea: z.enum(['full', 'top', 'bottom', 'custom']),
});
export type Product = z.infer<typeof ProductSchema>;

export const CalendarSettingsSchema = z.object({
  weekStart: z.enum(['monday', 'sunday']),
  rows: z.enum(['fixed6', 'actual']),
  adjacent: z.enum(['blank', 'show']),
  weekNumbers: z.boolean(),
  locale: z.string(),
});
export type CalendarSettings = z.infer<typeof CalendarSettingsSchema>;

export const BrandSchema = z.object({
  presetId: z.string().nullable(),
  customerName: z.string().max(200),
  logoAssetId: z.string().nullable(),
  contact: z.string().max(1000),
  colors: z.array(ColorSchema).max(12),
  company: z.string().max(200),
  tagline: z.string().max(300),
  phone: z.string().max(100),
  email: z.string().max(200),
  website: z.string().max(200),
  address: z.string().max(500),
  /** e.g. an estate agent's name and job title */
  personName: z.string().max(200),
  personTitle: z.string().max(200),
});
export type Brand = z.infer<typeof BrandSchema>;

/** Layout grid for placing blocks: columns × rows inside the safe area (clear of binding/rim zones). */
export const LayoutGridSchema = z.object({
  cols: z.number().int().min(1).max(48),
  rows: z.number().int().min(1).max(48),
  gutterMm: z.number().min(0).max(50),
  /** snap blocks to whole cells while dragging/resizing (hold Alt for free placement) */
  snap: z.boolean(),
  /** show the grid on the page in the editor */
  show: z.boolean(),
});
export type LayoutGrid = z.infer<typeof LayoutGridSchema>;

export const ProjectDocSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  year: z.number().int().min(MIN_YEAR).max(MAX_YEAR),
  templateId: z.string(),
  product: ProductSchema,
  calendar: CalendarSettingsSchema,
  /** info: the tent's base / side panel with other information (holiday lists, notes) */
  designs: z.object({ month: PageDesignSchema, cover: PageDesignSchema, backing: PageDesignSchema, year: PageDesignSchema, face: PageDesignSchema, carrier: PageDesignSchema, info: PageDesignSchema }),
  /** Keys: "cover", "m01".."m12", "back", "backing", "year", "faceA", "faceB", "carrier". */
  overrides: z.record(z.string(), PageOverrideSchema),
  /** pageKey (or "shared" for frames shown on every page) → slot → placement */
  photos: z.record(z.string(), z.record(z.string(), ImagePlacementSchema)),
  /** Uploaded photo pool for this project, in upload order. */
  photoPool: z.array(z.string()),
  captions: z.record(z.string(), z.string().max(500)),
  brand: BrandSchema,
  eventSets: z.array(EventSetSchema),
  /** Explicit per-day background colour, highest background priority. */
  cellOverrides: z.record(z.string().refine(isValidDateKey), z.object({ fill: ColorSchema })),
  /** fontId → sha256 of the exact font file used. */
  fontLock: z.record(z.string(), z.string()),
  layoutGrid: LayoutGridSchema,
  checks: z.object({
    ppiTarget: z.number().min(50).max(1200),
    ppiMinimum: z.number().min(0).max(1200),
    acknowledged: z.array(z.string()),
  }),
});
export type ProjectDoc = z.infer<typeof ProjectDocSchema>;

/**
 * A design saved as a reusable template. Builder templates also carry the
 * product (size, orientation, calendar type, clearance …) and layout grid, and
 * are only offered for that size and orientation.
 */
export const SavedTemplateSchema = z.object({
  baseTemplateId: z.string(),
  size: z.object({ w: z.number().positive(), h: z.number().positive() }),
  designs: z.object({
    month: PageDesignSchema, cover: PageDesignSchema, backing: PageDesignSchema,
    year: PageDesignSchema.optional(), face: PageDesignSchema.optional(), carrier: PageDesignSchema.optional(), info: PageDesignSchema.optional(),
  }),
  product: ProductSchema.partial().optional(),
  layoutGrid: LayoutGridSchema.optional(),
});
export type SavedTemplate = z.infer<typeof SavedTemplateSchema>;

export type PageKey = string;
export const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => `m${String(i + 1).padStart(2, '0')}`);
export const monthOfKey = (k: PageKey): number | null => (/^m\d\d$/.test(k) ? +k.slice(1) : null);

/** Validate a document. Returns readable error strings. */
export function validateDoc(doc: unknown): { ok: true; doc: ProjectDoc } | { ok: false; errors: string[] } {
  const r = ProjectDocSchema.safeParse(doc);
  if (r.success) {
    const errors: string[] = [];
    for (const [key, d] of Object.entries(r.data.designs)) {
      const ids = new Set<string>();
      for (const el of d.elements) {
        if (ids.has(el.id)) errors.push(`designs.${key}: duplicate element id ${el.id}`);
        ids.add(el.id);
      }
    }
    return errors.length ? { ok: false, errors } : { ok: true, doc: r.data };
  }
  return { ok: false, errors: r.error.issues.slice(0, 20).map((i) => `${i.path.join('.')}: ${i.message}`) };
}

